import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useToast } from '@/shared/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { usePresetGalleries } from './usePresetGalleries';
import { useUploadPresetGallery } from './useUploadPresetGallery';
import { useDeletePresetGallery } from './useDeletePresetGallery';
import type { GalleryItem } from '../types';
import { MAX_FILE_SIZE_BYTES, getFileSizeErrorMessage } from '@/shared/lib/fileUploadConstants';
import { useAuth } from '@/features/auth/model/AuthContext';
import { presetKeys } from './preset.keys';

interface LocalGalleryItem extends GalleryItem {
  beforeFile?: File;
  afterFile?: File;
}

export function useGalleryOperations(
  presetId: string | null,
  isEditing: boolean = false
) {
  const [galleryItems, setGalleryItems] = useState<LocalGalleryItem[]>([
    { id: uuidv4(), before: null, after: null },
  ]);
  const { toast } = useToast();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { galleries: fetchedGalleries, isLoading: isFetching } = usePresetGalleries(
    presetId ?? undefined,
    { enabled: !!presetId && isEditing }
  );

  const { uploadGalleryItem, mutationResult: uploadGalleryResult } = useUploadPresetGallery(presetId ?? undefined);
  const { deleteGallery } = useDeletePresetGallery(presetId ?? undefined);
  const isUploadingGallery = uploadGalleryResult.isPending;

  const [galleryItemsToDelete, setGalleryItemsToDelete] = useState<string[]>([]);

  useEffect(() => {
    if (fetchedGalleries && fetchedGalleries.length > 0) {
      setGalleryItems(
        fetchedGalleries.map((item) => ({
          id: item.id,
          before: item.before,
          after: item.after,
        }))
      );
    }
  }, [fetchedGalleries]);

  const markGalleryItemForDelete = (id: string) => {
    setGalleryItems((prev) => prev.filter((item) => item.id !== id));
    setGalleryItemsToDelete((prev) => [...prev, id]);
  };

  const commitGalleryDeletions = async () => {
    for (const galleryUuid of galleryItemsToDelete) {
      try {
        await deleteGallery(galleryUuid);
      } catch {
        toast({
          title: 'Erro ao remover galeria',
          description: 'Não foi possível remover a galeria.',
          variant: 'destructive',
        });
      }
    }
    setGalleryItemsToDelete([]);
    if (presetId) {
      queryClient.invalidateQueries({ queryKey: presetKeys.galleries(presetId) });
    }
  };

  const addGalleryItem = () => {
    setGalleryItems((prev) => [
      ...prev,
      { id: uuidv4(), before: null, after: null },
    ]);
  };

  const handleGalleryPhotoChange = async (
    itemId: string,
    type: 'before' | 'after',
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      toast({
        title: 'Erro ao carregar imagem',
        description: getFileSizeErrorMessage(),
        variant: 'destructive',
      });
      e.target.value = '';
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      const preview = reader.result as string;
      setGalleryItems((prev) =>
        prev.map((item) =>
          item.id === itemId
            ? { ...item, [type]: preview, [`${type}File`]: file }
            : item
        )
      );
    };
    reader.readAsDataURL(file);
  };

  const uploadGalleryImages = async (presetId: string): Promise<GalleryItem[]> => {
    if (!presetId) {
      console.error('Preset ID is required to upload gallery images');
      return galleryItems;
    }

    const itemsToProcess = galleryItems.filter(
      (item) => item.beforeFile && item.afterFile
    );
    if (itemsToProcess.length === 0) {
      return [];
    }

    try {
      const uploaded: GalleryItem[] = [];
      for (const item of itemsToProcess) {
        await uploadGalleryItem({
          beforeFile: item.beforeFile!,
          afterFile: item.afterFile!,
          data: {
            presetId,
            before: '',
            after: '',
            createdById: user.id,
            displayOrder: 1,
          },
        });
        uploaded.push({
          id: item.id,
          before: item.before,
          after: item.after,
        });
      }
      return uploaded;
    } catch (error) {
      console.error('Error uploading gallery images:', error);
      toast({
        title: 'Erro ao fazer upload das imagens',
        description: 'Ocorreu um erro ao fazer upload das imagens da galeria',
        variant: 'destructive',
      });
      return [];
    }
  };

  return {
    galleryItems,
    setGalleryItems,
    isLoading: isFetching,
    isUploadingGallery,
    addGalleryItem,
    removeGalleryItem: markGalleryItemForDelete,
    handleGalleryPhotoChange,
    uploadGalleryImages,
    commitGalleryDeletions,
    galleryItemsToDelete,
  };
}
